import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChartIcon, CreditCardIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon, MegaphoneIcon, HomeIcon, BedDoubleIcon, ClipboardListIcon, DollarSignIcon, BookOpenIcon } from 'lucide-react';
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingsExpanded, setBookingsExpanded] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path: string) => {
    if (path === '/overview') {
      return location.pathname === '/overview' || location.pathname === '/';
    }
    return location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return location.pathname === '/payment-requests';
  };
  // Check if any bookings path is active
  const isBookingsActive = () => {
    return ['/property-information', '/rooms', '/inventory', '/rates', '/reservations'].includes(location.pathname);
  };
  // Get the current page title based on the URL
  const getPageTitle = () => {
    if (location.pathname === '/overview' || location.pathname === '/') return 'Overview';
    if (location.pathname === '/payment-requests') return 'Payment Requests';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/orders') return 'Orders';
    if (location.pathname === '/tutorials') return 'Tutorials';
    if (location.pathname === '/campaigns') return 'Campaigns';
    if (location.pathname === '/campaign-details') return 'Campaign Details';
    if (location.pathname === '/campaign-form') return location.search.includes('id=') ? 'Update Campaign' : 'Create Campaign';
    if (location.pathname === '/property-information') return 'Property Information';
    if (location.pathname === '/rooms') return 'Rooms';
    if (location.pathname === '/inventory') return 'Inventory';
    if (location.pathname === '/rates') return 'Rates';
    if (location.pathname === '/reservations') return 'Reservations';
    if (location.pathname.startsWith('/tutorial/')) return 'Tutorial';
    return 'Dashboard';
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-84">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 right-0 p-4 z-20" data-id="element-85">
        <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-md bg-white shadow-sm border border-gray-200" data-id="element-86">
          {menuOpen ? <XIcon size={20} data-id="element-87" /> : <MenuIcon size={20} data-id="element-88" />}
        </button>
      </div>
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 w-64 bg-[#1a2042] z-10 transform transition-transform duration-200 ease-in-out
        ${menuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-id="element-89">
        <div className="h-full flex flex-col" data-id="element-90">
          {/* Logo */}
          <div className="p-4 border-b border-[#2a3154] flex justify-center items-center" data-id="element-91">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bVuihrN5RNgkp1vjqpMypS/sidebar-logo.svg" alt="Rabbit" className="h-10" data-id="element-92" />
          </div>
          {/* Navigation */}
          <nav className="flex-1 py-4 space-y-1 text-gray-300" data-id="element-93">
            <button onClick={() => navigate('/overview')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/overview') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-94">
              <BarChartIcon size={18} className="mr-3" data-id="element-95" />
              Overview
            </button>
            <button onClick={() => navigate('/payment-requests')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/payment-requests') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-96">
              <LayoutGridIcon size={18} className="mr-3" data-id="element-97" />
              Payment requests
            </button>
            {/* Terminals Collapsible Menu */}
            <div data-id="element-98">
              <button onClick={() => setTerminalsExpanded(!terminalsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-99">
                <div className="flex items-center" data-id="element-100">
                  <TerminalIcon size={18} className="mr-3" data-id="element-101" />
                  Terminals
                </div>
                <ChevronDownIcon size={16} className={`transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} data-id="element-102" />
              </button>
              {/* Terminal submenu items */}
              {terminalsExpanded && <div className="ml-7 pl-4 border-l border-[#2a3154]" data-id="element-103">
                  <button onClick={() => navigate('/virtual-terminal')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/virtual-terminal') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-104">
                    <BanknoteIcon size={16} className="mr-3" data-id="element-105" />
                    Virtual
                  </button>
                  <button onClick={() => navigate('/physical-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/physical-terminals') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-106">
                    <CreditCardIcon size={16} className="mr-3" data-id="element-107" />
                    Physical
                  </button>
                  <button onClick={() => navigate('/softpos-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/softpos-terminals') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-108">
                    <SmartphoneIcon size={16} className="mr-3" data-id="element-109" />
                    SoftPos
                  </button>
                  <button onClick={() => navigate('/connect')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/connect') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-110">
                    <GlobeIcon size={16} className="mr-3" data-id="element-111" />
                    Connect
                  </button>
                </div>}
            </div>
            <button onClick={() => navigate('/invoices')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/invoices') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-112">
              <FileTextIcon size={18} className="mr-3" data-id="element-113" />
              Invoices
            </button>
            <div className="border-t border-[#2a3154]" data-id="element-114"></div>
            {/* Bookings Collapsible Menu */}
            <div data-id="element-115">
              <button onClick={() => setBookingsExpanded(!bookingsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isBookingsActive() ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-116">
                <div className="flex items-center" data-id="element-117">
                  <CalendarIcon size={18} className="mr-3" data-id="element-118" />
                  Bookings
                </div>
                <ChevronDownIcon size={16} className={`transition-transform ${bookingsExpanded ? 'rotate-180' : ''}`} data-id="element-119" />
              </button>
              {/* Bookings submenu items */}
              {bookingsExpanded && <div className="ml-7 pl-4 border-l border-[#2a3154]" data-id="element-120">
                  <button onClick={() => navigate('/property-information')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/property-information') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-121">
                    <HomeIcon size={16} className="mr-3" data-id="element-122" />
                    Property Information
                  </button>
                  <button onClick={() => navigate('/rooms')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/rooms') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-123">
                    <BedDoubleIcon size={16} className="mr-3" data-id="element-124" />
                    Rooms
                  </button>
                  <button onClick={() => navigate('/inventory')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/inventory') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-125">
                    <ClipboardListIcon size={16} className="mr-3" data-id="element-126" />
                    Inventory
                  </button>
                  <button onClick={() => navigate('/rates')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/rates') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-127">
                    <DollarSignIcon size={16} className="mr-3" data-id="element-128" />
                    Rates
                  </button>
                  <button onClick={() => navigate('/reservations')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/reservations') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-129">
                    <BookOpenIcon size={16} className="mr-3" data-id="element-130" />
                    Reservations
                  </button>
                </div>}
            </div>
            <button onClick={() => navigate('/customers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/customers') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-131">
              <UsersIcon size={18} className="mr-3" data-id="element-132" />
              Customers
            </button>
            <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/transactions') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-133">
              <div className="flex items-center" data-id="element-134">
                <ArrowLeftRightIcon size={18} className="mr-3" data-id="element-135" />
                Transactions
              </div>
              <ChevronDownIcon size={16} className={`transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} data-id="element-136" />
            </button>
            <button onClick={() => navigate('/shops')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/shops') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-137">
              <ShoppingBagIcon size={18} className="mr-3" data-id="element-138" />
              Shops
            </button>
            <button onClick={() => navigate('/orders')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/orders') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-139">
              <ShoppingCartIcon size={18} className="mr-3" data-id="element-140" />
              Orders
            </button>
            <button onClick={() => navigate('/campaigns')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-141">
              <MegaphoneIcon size={18} className="mr-3" data-id="element-142" />
              Campaigns
            </button>
            <div className="border-t border-[#2a3154]" data-id="element-143"></div>
            <button onClick={() => navigate('/settings')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/settings') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-144">
              <SettingsIcon size={18} className="mr-3" data-id="element-145" />
              Settings
            </button>
            <button onClick={() => navigate('/tutorials')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/tutorials') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-146">
              <GraduationCapIcon size={18} className="mr-3" data-id="element-147" />
              Tutorials
            </button>
          </nav>
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 lg:pl-64" data-id="element-148">
        <div className="bg-white border-b border-gray-200" data-id="element-149">
          <div className="flex justify-between items-center px-6 py-4" data-id="element-150">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-151">
              {getPageTitle()}
            </h1>
            <div className="flex items-center" data-id="element-152">
              <div className="w-10 h-10 bg-[#f8d7d7] rounded-full flex items-center justify-center text-[#e54c3c] font-medium" data-id="element-153">
                SV
              </div>
              <div className="ml-3 hidden sm:block" data-id="element-154">
                <p className="text-sm font-medium text-gray-800" data-id="element-155">
                  Simon Verraest
                </p>
                <p className="text-xs text-gray-500" data-id="element-156">BML EXPERIENCE ZONE</p>
              </div>
              <ChevronDownIcon size={16} className="ml-2 text-gray-500" data-id="element-157" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-158">
          {children}
        </div>
      </div>
      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-0 lg:hidden" onClick={() => setMenuOpen(false)} data-id="element-159"></div>}
    </div>;
}